import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { getIndicator } from '../indicators/registry';
import { sessions as sessionsApi } from '../api/client';

const SAVE_DEBOUNCE = 2000; // save config 2s after last change

/**
 * Hook to manage active indicators and compute their data from visible candles.
 *
 * If sessionId is provided, loads and persists indicator config.
 */
export default function useIndicators(visibleCandles, sessionId, initialConfig) {
  const [activeIndicators, setActiveIndicators] = useState([]);
  const saveTimerRef = useRef(null);
  const pendingConfigRef = useRef(null);
  const initializedRef = useRef(false);

  // Load initial config from session
  useEffect(() => {
    if (initialConfig && initialConfig.length > 0 && !initializedRef.current) {
      setActiveIndicators(initialConfig);
      initializedRef.current = true;
    }
  }, [initialConfig]);

  // Flush any pending debounced save immediately
  const flushSave = useCallback(() => {
    if (!sessionId || !pendingConfigRef.current) return Promise.resolve();
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    const config = pendingConfigRef.current;
    pendingConfigRef.current = null;
    return sessionsApi.saveIndicatorConfig(sessionId, config).catch((err) => {
      console.error('Failed to save indicator config:', err);
    });
  }, [sessionId]);

  // Save config to server (debounced)
  const saveConfig = useCallback(
    (indicators) => {
      if (!sessionId) return;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      pendingConfigRef.current = indicators.map((ai) => ({
        indicatorId: ai.indicatorId,
        instanceId: ai.instanceId,
        params: ai.params,
      }));
      saveTimerRef.current = setTimeout(() => {
        const config = pendingConfigRef.current;
        pendingConfigRef.current = null;
        sessionsApi.saveIndicatorConfig(sessionId, config).catch((err) => {
          console.error('Failed to save indicator config:', err);
        });
      }, SAVE_DEBOUNCE);
    },
    [sessionId]
  );

  // Flush pending save on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (pendingConfigRef.current && sessionId) {
        const config = pendingConfigRef.current;
        pendingConfigRef.current = null;
        sessionsApi.saveIndicatorConfig(sessionId, config).catch(() => {});
      }
    };
  }, [sessionId]);

  const addIndicator = useCallback(
    (config) => {
      setActiveIndicators((prev) => {
        const next = [...prev, config];
        saveConfig(next);
        return next;
      });
    },
    [saveConfig]
  );

  const removeIndicator = useCallback(
    (instanceId) => {
      setActiveIndicators((prev) => {
        const next = prev.filter((a) => a.instanceId !== instanceId);
        saveConfig(next);
        return next;
      });
    },
    [saveConfig]
  );

  const updateIndicatorParams = useCallback(
    (instanceId, newParams) => {
      setActiveIndicators((prev) => {
        const next = prev.map((a) =>
          a.instanceId === instanceId
            ? { ...a, params: { ...a.params, ...newParams } }
            : a
        );
        saveConfig(next);
        return next;
      });
    },
    [saveConfig]
  );

  const setIndicators = useCallback(
    (indicators) => {
      setActiveIndicators(indicators);
      saveConfig(indicators);
    },
    [saveConfig]
  );

  // Compute indicator data
  const computedData = useMemo(() => {
    if (!visibleCandles || visibleCandles.length === 0) return [];

    return activeIndicators.map((ai) => {
      const indicator = getIndicator(ai.indicatorId);
      if (!indicator) return { ...ai, results: {}, indicator: null };

      try {
        const results = indicator.compute(visibleCandles, ai.params);
        return { ...ai, results, indicator };
      } catch (err) {
        console.error(`Error computing ${ai.indicatorId}:`, err);
        return { ...ai, results: {}, indicator };
      }
    });
  }, [activeIndicators, visibleCandles]);

  // Helper: get color for series i from instance params, falling back to registry
  const getColor = (cd, i) => cd.params[`color_${i}`] || cd.indicator.colors[i] || '#6366f1';
  const getLineWidth = (cd) => cd.params.lineWidth || 2;

  // Split into overlay series and panel data
  const overlayIndicators = useMemo(() => {
    const overlays = [];
    for (const cd of computedData) {
      if (!cd.indicator || cd.indicator.displayType !== 'overlay') continue;

      const seriesKeys = cd.indicator.series;

      for (let i = 0; i < seriesKeys.length; i++) {
        const key = seriesKeys[i];
        const data = cd.results[key];
        if (data && data.length > 0) {
          overlays.push({
            seriesKey: `${cd.instanceId}_${key}`,
            data,
            color: getColor(cd, i),
            lineWidth: getLineWidth(cd),
            hidden: !!cd.params[`hide_${i}`],
          });
        }
      }
    }
    return overlays;
  }, [computedData]);

  const panelIndicators = useMemo(() => {
    const panels = [];

    for (const cd of computedData) {
      if (!cd.indicator || cd.indicator.displayType !== 'panel') continue;

      const seriesKeys = cd.indicator.series;

      const seriesData = [];
      for (let i = 0; i < seriesKeys.length; i++) {
        const key = seriesKeys[i];
        const data = cd.results[key];
        if (data && data.length > 0) {
          // No padding needed — single chart shares the same timeScale,
          // so timestamps align automatically with candles.
          seriesData.push({
            key: `${cd.instanceId}_${key}`,
            data,
            color: getColor(cd, i),
            type: key === 'histogram' ? 'histogram' : 'line',
            hidden: !!cd.params[`hide_${i}`],
          });
        }
      }

      if (seriesData.length > 0) {
        panels.push({
          instanceId: cd.instanceId,
          title: cd.indicator.name,
          seriesData,
          levels: cd.indicator.levels || [],
        });
      }
    }
    return panels;
  }, [computedData]);

  return {
    activeIndicators,
    overlayIndicators,
    panelIndicators,
    addIndicator,
    removeIndicator,
    updateIndicatorParams,
    setIndicators,
    flushSave,
  };
}

/**
 * Pure function: compute overlay + panel indicator data for a given candle set.
 * Reusable for secondary timeframe charts without a full hook.
 */
export function computeIndicatorsForCandles(activeIndicators, candles) {
  if (!candles || candles.length === 0 || !activeIndicators || activeIndicators.length === 0) {
    return { overlays: [], panels: [] };
  }

  const computed = activeIndicators.map((ai) => {
    const indicator = getIndicator(ai.indicatorId);
    if (!indicator) return { ...ai, results: {}, indicator: null };
    try {
      return { ...ai, results: indicator.compute(candles, ai.params), indicator };
    } catch {
      return { ...ai, results: {}, indicator };
    }
  });

  const getColor = (cd, i) => cd.params[`color_${i}`] || cd.indicator.colors[i] || '#6366f1';
  const getLineWidth = (cd) => cd.params.lineWidth || 2;

  const overlays = [];
  for (const cd of computed) {
    if (!cd.indicator || cd.indicator.displayType !== 'overlay') continue;
    for (let i = 0; i < cd.indicator.series.length; i++) {
      const key = cd.indicator.series[i];
      const data = cd.results[key];
      if (data && data.length > 0) {
        overlays.push({
          seriesKey: `${cd.instanceId}_${key}`,
          data,
          color: getColor(cd, i),
          lineWidth: getLineWidth(cd),
          hidden: !!cd.params[`hide_${i}`],
        });
      }
    }
  }

  const panels = [];
  for (const cd of computed) {
    if (!cd.indicator || cd.indicator.displayType !== 'panel') continue;
    const seriesData = [];
    for (let i = 0; i < cd.indicator.series.length; i++) {
      const key = cd.indicator.series[i];
      const data = cd.results[key];
      if (data && data.length > 0) {
        seriesData.push({
          key: `${cd.instanceId}_${key}`,
          data,
          color: getColor(cd, i),
          type: key === 'histogram' ? 'histogram' : 'line',
          hidden: !!cd.params[`hide_${i}`],
        });
      }
    }
    if (seriesData.length > 0) {
      panels.push({
        instanceId: cd.instanceId,
        title: cd.indicator.name,
        seriesData,
        levels: cd.indicator.levels || [],
      });
    }
  }

  return { overlays, panels };
}
